/**
 * CoverageChip (DC-1492, reworked v2.5 SS-03 C2 — G0 §5.2).
 *
 * Canonical coverage chip for the Triangulated FAIR Assessment view.
 * Shipped here first per Anuj's lock 2026-05-14: the DC-1573 FAIR-report
 * visual upgrades import this same component so one source of truth covers
 * the assessment surface.
 *
 * C2: the Perceived leg carries a coverage BASIS (what the denominator IS),
 * and the chip renders the G0 §5.2 four-state matrix with its exact copy:
 *
 *   - basis 'unreviewed'           → "{n} of {m} domain-relevant personas —
 *                                    target population unreviewed"
 *   - basis 'too_few_contributors' → "{n} of {m} confirmed targets — fewer
 *                                    than 3 contributing personas, coverage
 *                                    preliminary"
 *   - basis 'confirmed' + preliminary → "{n} of {m} confirmed targets
 *                                    ({p}%) — coverage preliminary"
 *   - basis 'confirmed' + sufficient  → "{n} of {m} confirmed targets
 *                                    ({p}%) — coverage sufficient"
 *
 * Legs without a basis (Technical / Documented) keep the three-state
 * sufficiency rendering. G0 prohibits tooltip-only information — everything
 * is visible text; the null glyph is the em dash (—), never a value.
 */

import React from 'react';
import type { LegCoverage } from '../../types/triangulation';

interface CoverageChipProps {
  coverage: LegCoverage;
  /** When true, omits the count text — useful inside dense tables. */
  countless?: boolean;
}

const SUFFICIENCY_LABELS: Record<LegCoverage['sufficiency'], string> = {
  sufficient: 'Sufficient',
  preliminary: 'Preliminary',
  insufficient: 'Insufficient',
};

/** The chip's visual state — G0 §5.2 for based legs, sufficiency otherwise. */
function chipState(coverage: LegCoverage): {
  state: string;
  label: string;
  detail: string | null;
} {
  const pctText = coverage.pct !== null ? `${Math.round(coverage.pct * 100)}%` : '—';
  if (coverage.basis === 'unreviewed') {
    return {
      state: 'unknown',
      label: 'Unreviewed',
      detail: `${coverage.evaluated} of ${coverage.total} domain-relevant personas — target population unreviewed`,
    };
  }
  if (coverage.basis === 'too_few_contributors') {
    return {
      state: 'warn',
      label: 'Too few contributors',
      detail: `${coverage.evaluated} of ${coverage.total} confirmed targets — fewer than 3 contributing personas, coverage preliminary`,
    };
  }
  if (coverage.basis === 'confirmed') {
    const word = coverage.sufficiency === 'sufficient' ? 'sufficient' : 'preliminary';
    return {
      state: coverage.sufficiency === 'sufficient' ? 'pass' : 'warn',
      label: SUFFICIENCY_LABELS[coverage.sufficiency],
      detail: `${coverage.evaluated} of ${coverage.total} confirmed targets (${pctText}) — coverage ${word}`,
    };
  }
  return {
    state: coverage.sufficiency,
    label: SUFFICIENCY_LABELS[coverage.sufficiency],
    detail: `${coverage.evaluated}/${coverage.total} · ${pctText}`,
  };
}

export const CoverageChip: React.FC<CoverageChipProps> = ({ coverage, countless = false }) => {
  const { state, label, detail } = chipState(coverage);

  return (
    <span
      data-testid="coverage-chip"
      data-sufficiency={coverage.sufficiency}
      data-basis={coverage.basis ?? 'none'}
      data-state={state}
      className="tri-cov"
    >
      <span>{label}</span>
      {!countless && detail && <span className="tri-cov-count">{detail}</span>}
    </span>
  );
};
